import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users, Megaphone, FileText, Landmark, Calendar, Mail, Bell, LogOut, Command as CmdIcon, CheckSquare, Inbox, MessageSquare, MessageCircle, Vote, MailPlus, Beaker, Activity, ShieldCheck } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import CommandPalette from "@/components/admin/CommandPalette";
import BudgetBanner from "@/components/admin/BudgetBanner";
import { AssistantWidget } from "@/components/AssistantWidget";
import { listNotifications } from "@/lib/api";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox, badge: "notifications" },
  { to: "/admin/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/admin/conversations", label: "Conversations", icon: MessageSquare },
  { to: "/admin/public-themes", label: "Public Themes", icon: MessageCircle },
  { to: "/admin/contacts", label: "Contacts", icon: Users },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/admin/email-templates", label: "Templates", icon: MailPlus },
  { to: "/admin/bills", label: "Bills", icon: Landmark },
  { to: "/admin/legislators", label: "Legislators", icon: Vote },
  { to: "/admin/statements", label: "Statements", icon: FileText },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/emails", label: "Emails", icon: Mail },
  { to: "/admin/experiments", label: "Experiments", icon: Beaker },
  { to: "/admin/automations", label: "Automations", icon: Activity },
  { to: "/admin/announcement", label: "Announcement", icon: Bell },
  { to: "/admin/team", label: "Team", icon: ShieldCheck, adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await listNotifications({ only_unread: true, limit: 1 });
        if (!cancelled) setUnread(r.unread_count || 0);
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 45000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <div className="min-h-screen flex bg-ccc-ivory" data-testid="admin-shell">
      <aside className="w-64 bg-ccc-plum text-ccc-ivory flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-6 border-b border-purple-950">
          <Link to="/" className="font-serif text-xl text-ccc-gold leading-tight block">
            Colorado Catholic<br/>Conference
          </Link>
          <div className="text-xs text-stone-300 mt-1">Advocacy CRM</div>
        </div>
        <nav className="flex-1 py-4 flex flex-col gap-1 overflow-y-auto">
          {items.filter(i => !i.adminOnly || user?.role === "Admin").map(i => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.end}
              data-testid={`admin-nav-${i.label.toLowerCase()}`}
              className={({ isActive }) => `mx-3 px-3 py-2 flex items-center gap-3 text-sm rounded-sm transition-colors ${
                isActive ? "bg-ccc-purple text-white" : "text-stone-200 hover:bg-purple-900"
              }`}
            >
              <i.icon size={16}/>
              <span className="flex-1">{i.label}</span>
              {i.badge === "notifications" && unread > 0 && (
                <span
                  data-testid="nav-unread-badge"
                  className="bg-ccc-gold text-ccc-plum text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-purple-950">
          {user && (
            <div className="mb-3">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-stone-400 truncate">{user.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-ccc-gold mt-1">{user.role}</div>
            </div>
          )}
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 text-sm text-stone-300 hover:text-white px-2 py-1.5"
          >
            <LogOut size={14}/> Sign out
          </button>
          <div className="mt-3 text-[10px] uppercase tracking-wider text-stone-400 flex items-center gap-1">
            <CmdIcon size={10}/> Press <kbd className="px-1 border border-stone-500 rounded-sm">⌘K</kbd> to search
          </div>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <BudgetBanner/>
        <main className="flex-1 p-8 overflow-x-hidden">
          <Outlet/>
        </main>
      </div>
      <CommandPalette/>
      <AssistantWidget mode="admin"/>
      <Toaster position="top-right"/>
    </div>
  );
}
